import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAnnouncements } from '../context/AnnouncementContext';
import logo from '../media/logo_dentiste.png';
import logoWhite from '../media/logo_white.png';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { bannerHeight } = useAnnouncements();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Helper function to get the active class for navigation links
  const getNavLinkClass = (path: string) => {
    const isActive = location.pathname === path || 
                     (path !== '/' && location.pathname.startsWith(path));
    return isActive ? 'text-primary border-b-2 border-primary pb-1' : 'text-gray-700 hover:text-primary';
  };

  const isHomePage = location.pathname === '/';
  const currentLogo = isHomePage && !isScrolled ? logoWhite : logo;

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isHomePage && !isScrolled 
          ? 'bg-transparent py-6' 
          : 'bg-white shadow-elegant py-3'
      }`}
      style={{ top: `${bannerHeight}px` }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group" onClick={scrollToTop}>
              <img 
                src={currentLogo} 
                alt="Dr. Banelhaq Ayoub - Cabinet Dentaire" 
                className="h-12 w-auto transition-transform group-hover:scale-110" 
              />
              <div className="ml-3">
                <span className={`text-xl font-semibold ${isHomePage && !isScrolled ? 'text-white' : 'text-text-dark'}`}>
                  Dr. Banelhaq Ayoub
                </span>
                <p className={`text-xs font-medium ${isHomePage && !isScrolled ? 'text-gray-200' : 'text-gray-500'}`}>
                  Cabinet Dentaire
                </p>
              </div>
            </Link>
          </div>
          
          {/* Menu pour desktop */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors ${
                isHomePage && !isScrolled 
                  ? 'text-white hover:text-blue-light' 
                  : getNavLinkClass('/')
              }`}
              onClick={scrollToTop}
            >
              Accueil
            </Link>
            <Link 
              to="/services" 
              className={`text-sm font-medium transition-colors ${
                isHomePage && !isScrolled 
                  ? 'text-white hover:text-blue-light' 
                  : getNavLinkClass('/services')
              }`}
              onClick={scrollToTop}
            >
              Services
            </Link>
            <Link 
              to="/rendez-vous" 
              className={`text-sm font-medium transition-colors ${
                isHomePage && !isScrolled 
                  ? 'text-white hover:text-blue-light' 
                  : getNavLinkClass('/rendez-vous')
              }`}
              onClick={scrollToTop}
            >
              Rendez-vous
            </Link>
            <Link 
              to="/contact" 
              className={`text-sm font-medium transition-colors ${
                isHomePage && !isScrolled 
                  ? 'text-white hover:text-blue-light' 
                  : getNavLinkClass('/contact')
              }`}
              onClick={scrollToTop}
            >
              Contact
            </Link>
            
            <a 
              href="https://wa.me/212684707407" 
              target="_blank"
              rel="noopener noreferrer"
              className={`btn-outline flex items-center ${
                isHomePage && !isScrolled 
                  ? 'border-white text-white hover:bg-white hover:text-accent' 
                  : ''
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
          </div>
          
          {/* Bouton menu mobile */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className={`inline-flex items-center justify-center p-2 rounded-md ${
                isHomePage && !isScrolled 
                  ? 'text-white hover:text-blue-light' 
                  : 'text-gray-700 hover:text-primary'
              } focus:outline-none`}
            >
              <FontAwesomeIcon icon={isOpen ? faTimes : faBars} className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Menu mobile */}
      <div className={`md:hidden absolute top-full left-0 w-full bg-white shadow-elegant border-t border-gray-200 transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible h-0'}`}>
        <div className="px-4 py-6 space-y-6">
          <Link 
            to="/" 
            className={`block ${location.pathname === '/' ? 'text-primary font-medium' : 'text-gray-800 hover:text-primary'}`}
            onClick={() => {
              setIsOpen(false);
              scrollToTop();
            }}
          >
            Accueil
          </Link>
          <Link 
            to="/services" 
            className={`block ${location.pathname === '/services' || location.pathname.startsWith('/services/') ? 'text-primary font-medium' : 'text-gray-800 hover:text-primary'}`}
            onClick={() => {
              setIsOpen(false);
              scrollToTop();
            }}
          >
            Services
          </Link>
          <Link 
            to="/rendez-vous" 
            className={`block ${location.pathname === '/rendez-vous' ? 'text-primary font-medium' : 'text-gray-800 hover:text-primary'}`}
            onClick={() => {
              setIsOpen(false);
              scrollToTop();
            }}
          >
            Rendez-vous
          </Link>
          <Link 
            to="/contact" 
            className={`block ${location.pathname === '/contact' ? 'text-primary font-medium' : 'text-gray-800 hover:text-primary'}`}
            onClick={() => {
              setIsOpen(false);
              scrollToTop();
            }}
          >
            Contact
          </Link>
          <a 
            href="https://wa.me/212684707407" 
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline flex items-center justify-center"
            onClick={() => setIsOpen(false)}
          >
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 